/**
 * Storezone — App Router
 *
 * Route Architecture:
 *   /auth/*       → AuthLayout  (public)
 *   /platform/*   → PlatformLayout + superadmin theme (requires superadmin)
 *   /admin/*      → AdminLayout  + tenant/storezone theme (requires tenant member)
 *   /pos/*        → SellerLayout + tenant theme (requires seller role)
 *   /             → Smart redirect based on user role
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AuthProvider } from '@/contexts/AuthContext'
import { TenantProvider } from '@/contexts/TenantContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ProgressProvider } from '@/contexts/ProgressContext'
import { AlertProvider } from '@/contexts/AlertContext'

import { GlobalProgressBar } from '@/components/ui'
import { ProtectedRoute } from '@/components/ProtectedRoute'

import { LoginPage } from '@/pages/auth/LoginPage'
import { PlatformRoutes } from '@/routes/platformRoutes'
import { AdminRoutes } from '@/routes/adminRoutes'
import { SellerRoutes } from '@/routes/sellerRoutes'
import { RootRedirect } from '@/components/RootRedirect'

export default function App() {
  return (
    <ThemeProvider initialTheme="storezone">
      <ProgressProvider>
        <BrowserRouter>
          {/* Global top-of-page progress bar */}
          <GlobalProgressBar />

          <AuthProvider>
            <TenantProvider>
              <AlertProvider>
                <Routes>
                {/* ─── Public ─── */}
                <Route path="/auth/login" element={<LoginPage />} />
                {/* Legacy redirect */}
                <Route path="/login" element={<Navigate to="/auth/login" replace />} />

                {/* ─── Protected ─── */}
                <Route element={<ProtectedRoute />}>

                  {/* Superadmin zone */}
                  <Route
                    path="/platform/*"
                    element={
                      <ThemeProvider initialTheme="superadmin">
                        <PlatformRoutes />
                      </ThemeProvider>
                    }
                  />

                  {/* Admin / Tenant zone */}
                  <Route path="/admin/*" element={<AdminRoutes />} />

                  {/* Seller / POS zone */}
                  <Route path="/pos/*" element={<SellerRoutes />} />

                  {/* Smart root redirect */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Legacy dashboard redirect */}
                  <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
                </Route>

                {/* ─── Catch-all ─── */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AlertProvider>
            </TenantProvider>
          </AuthProvider>
        </BrowserRouter>
      </ProgressProvider>
    </ThemeProvider>
  )
}
